import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, AlertTriangle, FileText } from 'lucide-react';

const fmtDate = (v) => {
  if (!v) return null;
  try {
    return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return v; }
};

const Row = ({ label, value, testid }) => {
  if (value === null || value === undefined || value === '' ) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/40 last:border-0" data-testid={testid}>
      <p className="text-xs text-muted-foreground shrink-0 max-w-[55%]">{label}</p>
      <p className="text-xs font-medium text-right">{String(value)}</p>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <div className="mb-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{title}</p>
    <div className="rounded-xl border bg-card px-3 py-1">{children}</div>
  </div>
);

/** V70 — the mother's profile sheet for the dietician, web parity with the app.
 *  Renders only what the server sends; never shows an empty row. */
export const LearnerProfileSheet = ({ consultId, open, onOpenChange, onOpenVault }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !consultId || data) return;
    setLoading(true);
    api.get(`/api/medical/consult/${consultId}/learner-profile`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail?.message || 'Could not load her profile'))
      .finally(() => setLoading(false));
  }, [open, consultId, data]);

  const id = data?.identity || {};
  const preg = data?.pregnancy || {};
  const body = data?.body || {};
  const clin = data?.clinical || {};
  const life = data?.lifestyle || {};
  const flags = [...(clin.complications || []), ...(clin.conditions || [])];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="learner-profile-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {id.first_name || 'Her profile'}
          </DialogTitle>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {preg.week ? <Badge variant="secondary" data-testid="profile-week">Week {preg.week}</Badge> : null}
            {preg.trimester ? <Badge variant="outline">Trimester {preg.trimester}</Badge> : null}
            {preg.edd ? <Badge variant="outline" data-testid="profile-edd">Due {fmtDate(preg.edd)}</Badge> : null}
            {id.plan_label ? <Badge variant="outline">{id.plan_label}</Badge> : null}
            {preg.weeks_postpartum ? <Badge variant="secondary">{preg.weeks_postpartum} wks postpartum</Badge> : null}
          </div>
        </DialogHeader>

        {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
        {error && <p className="text-sm text-destructive py-6 text-center" data-testid="profile-error">{error}</p>}

        {data && !loading && (
          <div className="pt-1">
            {/* Flags first — these are what make a diet plan unsafe */}
            {flags.length > 0 && (
              <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="profile-flags">
                <div className="flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />
                  <p className="text-[11px] uppercase tracking-wider text-amber-900">Watch for</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {flags.map((c) => (
                    <Badge key={c.code} className="bg-amber-100 text-amber-900 hover:bg-amber-100"
                           data-testid={`profile-flag-${c.code}`}>{c.label}</Badge>
                  ))}
                </div>
              </div>
            )}

            <Section title="Pregnancy" testid="profile-section-pregnancy">
              <Row label="Due date" value={fmtDate(preg.edd)} />
              <Row label="Stage" value={preg.stage === 'postnatal' ? 'Postnatal' : 'Prenatal'} />
              <Row label="Pregnancy" value={preg.pregnancy_type_label} />
              <Row label="Conceived" value={preg.conceived_via_label} />
              <Row label="Delivery" value={preg.delivery_type_label} />
              <Row label="Delivered on" value={fmtDate(preg.delivery_date)} />
              <Row label="Risk level" value={preg.risk_level === 'high' ? 'High risk' : preg.risk_level === 'low' ? 'Low risk' : null} />
            </Section>

            {Object.keys(body).length > 0 && (
              <Section title="Body & weight" testid="profile-section-body">
                <Row label="Height" value={body.height_cm ? `${body.height_cm} cm` : null} />
                <Row label="Pre-pregnancy weight" value={body.pre_pregnancy_weight_kg ? `${body.pre_pregnancy_weight_kg} kg` : null} />
                <Row label="Current weight" value={body.current_weight_kg ? `${body.current_weight_kg} kg` : null} />
                <Row label="Weight as of" value={fmtDate(body.weight_logged_at)} />
                <Row label="Pre-pregnancy BMI" value={body.bmi_pre_pregnancy} />
              </Section>
            )}

            {Object.keys(life).length > 0 && (
              <Section title="Lifestyle" testid="profile-section-lifestyle">
                <Row label="Activity now" value={life.activity_level_label} />
                <Row label="Fitness" value={life.fitness_level_label} />
                <Row label="Practice days" value={life.practice_days} />
                <Row label="Classes a week" value={life.classes_per_week} />
                <Row label="Her goals" value={(life.goals || []).map((g) => g.label).join(', ') || null} />
              </Section>
            )}

            {(data.answers || []).length > 0 && (
              <Section title="What she told us" testid="profile-section-answers">
                {data.answers.map((a) => (
                  <Row key={a.key} label={a.question} value={a.answer} testid={`profile-answer-${a.key}`} />
                ))}
              </Section>
            )}

            {data.ai_intake_summary && (
              <details className="mb-4 rounded-xl border bg-muted/30 p-3" data-testid="profile-ai-summary">
                <summary className="text-[11px] uppercase tracking-wider text-muted-foreground cursor-pointer">
                  AI summary (not a clinical record)
                </summary>
                <p className="text-xs mt-2 leading-relaxed">{data.ai_intake_summary}</p>
              </details>
            )}

            {onOpenVault && (
              <button onClick={() => { onOpenChange(false); onOpenVault(); }}
                      className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                      data-testid="profile-open-vault">
                <FileText className="h-3.5 w-3.5" /> Shared documents
              </button>
            )}

            <p className="text-[10px] text-muted-foreground mt-4" data-testid="profile-freshness">
              {data.updated_at ? `Intake as of ${fmtDate(data.updated_at)}.` : ''} Read-only.
              Diet preferences, allergies and medication are not collected in the intake yet.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LearnerProfileSheet;
